import { BarcodeFormat, BrowserMultiFormatReader } from "@zxing/browser";

/** 1D-only — never QR (Cabex e-ticket screenshots contain Telegram/site QRs). */
const ONE_D_FORMATS = [
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.ITF,
  BarcodeFormat.UPC_A,
];

const ANY_SCAN_FORMATS = [BarcodeFormat.QR_CODE, ...ONE_D_FORMATS];

/** @zxing/library DecodeHintType numeric values (package not a direct dep). */
const HINT_POSSIBLE_FORMATS = 2;
const HINT_TRY_HARDER = 3;
/** Native BarcodeDetector format names (Chromium / Android WebView / MAX). */
const NATIVE_1D_FORMATS = [
  "ean_13",
  "ean_8",
  "code_128",
  "code_39",
  "upc_a",
  "upc_e",
  "itf",
] as const;

const NATIVE_ANY_FORMATS = ["qr_code", ...NATIVE_1D_FORMATS] as const;

type BarcodeDetectorLike = {
  detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string; format?: string }>>;
};

function getNativeDetector(formats: readonly string[]): BarcodeDetectorLike | null {
  const BD = (globalThis as unknown as { BarcodeDetector?: new (opts?: { formats?: string[] }) => BarcodeDetectorLike })
    .BarcodeDetector;
  if (!BD) return null;
  try {
    return new BD({ formats: [...formats] });
  } catch {
    try {
      return new BD();
    } catch {
      return null;
    }
  }
}

function scoreCandidate(text: string): number {
  const t = text.trim();
  if (!t) return -1;
  if (/^\d{12,}$/.test(t)) return 100 + t.length;
  if (/^\d{8,}$/.test(t)) return 50 + t.length;
  if (/^[A-Za-z0-9._-]{8,}$/.test(t)) return 20 + Math.min(t.length, 40);
  return t.length;
}

function pickBestCode(candidates: string[]): string | null {
  let best: string | null = null;
  let bestScore = -1;
  for (const raw of candidates) {
    const t = raw.trim();
    if (!t) continue;
    const s = scoreCandidate(t);
    if (s > bestScore) {
      bestScore = s;
      best = t;
    }
  }
  return best;
}

function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = url;
  });
}

function makeReader(formats: BarcodeFormat[]): BrowserMultiFormatReader {
  const hints = new Map<number, unknown>();
  hints.set(HINT_POSSIBLE_FORMATS, formats);
  hints.set(HINT_TRY_HARDER, true);
  const reader = new BrowserMultiFormatReader(hints as never);
  reader.possibleFormats = formats;
  return reader;
}

function tryDecodeCanvas(reader: BrowserMultiFormatReader, canvas: HTMLCanvasElement): string | null {
  try {
    return reader.decodeFromCanvas(canvas).getText().trim() || null;
  } catch {
    return null;
  }
}

function codeTypeFromText(text: string): "qr" | "barcode" {
  if (/^https?:\/\//i.test(text) || text.startsWith("g.")) return "qr";
  if (/^\d{8,}$/.test(text)) return "barcode";
  return text.length > 40 || text.includes("/") ? "qr" : "barcode";
}

function drawRegionFromCanvas(
  src: HTMLCanvasElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  const w = Math.max(1, Math.round(sw));
  const h = Math.max(1, Math.round(sh));
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");
  ctx.drawImage(src, sx, sy, sw, sh, 0, 0, w, h);
  return canvas;
}

/** Upscale + mild contrast — thin EAN bars on ~600px badge screenshots need it. */
function enhanceCanvas(src: HTMLCanvasElement, scale: number): HTMLCanvasElement {
  const w = Math.max(1, Math.round(src.width * scale));
  const h = Math.max(1, Math.round(src.height * scale));
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, 0, 0, w, h);
  try {
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    // Contrast stretch toward black/white (helps anti-aliased screen barcodes).
    for (let i = 0; i < d.length; i += 4) {
      const y = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      const v = y < 140 ? 0 : 255;
      d[i] = d[i + 1] = d[i + 2] = v;
    }
    ctx.putImageData(img, 0, 0);
  } catch {
    /* tainted / memory — keep color upscale */
  }
  return out;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error(label)), ms);
    promise.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(t);
        reject(e);
      },
    );
  });
}

async function decodeNativeFromBlob(
  file: Blob,
  formats: readonly string[],
): Promise<string | null> {
  const detector = getNativeDetector(formats);
  if (!detector) return null;
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImageFromUrl(url);
    const bitmap =
      typeof createImageBitmap === "function"
        ? await createImageBitmap(img)
        : null;
    const source: ImageBitmapSource = bitmap ?? img;
    const hits = await detector.detect(source);
    bitmap?.close?.();
    const texts = hits.map((h) => (h.rawValue || "").trim()).filter(Boolean);
    return pickBestCode(texts);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function decodeNativeFromCanvas(
  canvas: HTMLCanvasElement,
  formats: readonly string[],
): Promise<string | null> {
  const detector = getNativeDetector(formats);
  if (!detector) return null;
  try {
    const hits = await detector.detect(canvas);
    const texts = hits.map((h) => (h.rawValue || "").trim()).filter(Boolean);
    return pickBestCode(texts);
  } catch {
    return null;
  }
}

function collectZxingFromCanvas(
  reader: BrowserMultiFormatReader,
  canvas: HTMLCanvasElement,
  into: string[],
): void {
  const hit = tryDecodeCanvas(reader, canvas);
  if (hit) into.push(hit);
}

async function decodeWithFormats(file: Blob, formats: BarcodeFormat[]): Promise<string | null> {
  const nativeFormats =
    formats.includes(BarcodeFormat.QR_CODE) ? NATIVE_ANY_FORMATS : NATIVE_1D_FORMATS;

  // 1) Native BarcodeDetector — reads MVK badge screenshots that ZXing often misses.
  const nativeDirect = await decodeNativeFromBlob(file, nativeFormats);
  if (nativeDirect) return nativeDirect;

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImageFromUrl(url);
    const reader = makeReader(formats);
    const candidates: string[] = [];

    const rawW = img.naturalWidth || img.width;
    const rawH = img.naturalHeight || img.height;
    // Base: cap huge camera JPEGs; then also try upscales for small badge screenshots.
    const maxSide = 1600;
    const down = Math.min(1, maxSide / Math.max(rawW, rawH, 1));
    const baseW = Math.max(1, Math.round(rawW * down));
    const baseH = Math.max(1, Math.round(rawH * down));
    const base = document.createElement("canvas");
    base.width = baseW;
    base.height = baseH;
    const bctx = base.getContext("2d");
    if (!bctx) throw new Error("canvas_unavailable");
    bctx.drawImage(img, 0, 0, baseW, baseH);

    const variants: HTMLCanvasElement[] = [base];
    // Small digital badges (~500–700px): upscale so EAN bars are thick enough for ZXing.
    if (Math.max(rawW, rawH) < 1100) {
      variants.push(enhanceCanvas(base, 2));
      variants.push(enhanceCanvas(base, 2.5));
    } else {
      variants.push(enhanceCanvas(base, 1)); // contrast-only
    }

    const regionSpecs: Array<[number, number, number, number]> = [
      // Mid band where badge barcodes usually sit (short vs long FIO).
      [0.05, 0.25, 0.9, 0.5],
      [0.05, 0.3, 0.9, 0.45],
      [0.08, 0.35, 0.84, 0.4],
      [0.1, 0.4, 0.8, 0.4],
      [0, 0.2, 1, 0.55],
      [0, 0.45, 1, 0.5],
      [0.15, 0.15, 0.7, 0.7],
    ];

    for (const canvas of variants) {
      const nativeHit = await decodeNativeFromCanvas(canvas, nativeFormats);
      if (nativeHit) candidates.push(nativeHit);

      collectZxingFromCanvas(reader, canvas, candidates);

      const w = canvas.width;
      const h = canvas.height;
      for (const [rx, ry, rw, rh] of regionSpecs) {
        const crop = drawRegionFromCanvas(canvas, w * rx, h * ry, w * rw, h * rh);
        const nativeCrop = await decodeNativeFromCanvas(crop, nativeFormats);
        if (nativeCrop) candidates.push(nativeCrop);
        collectZxingFromCanvas(reader, crop, candidates);
        // Extra upscale on the barcode band alone.
        if (crop.width < 900) {
          const up = enhanceCanvas(crop, 2);
          const nativeUp = await decodeNativeFromCanvas(up, nativeFormats);
          if (nativeUp) candidates.push(nativeUp);
          collectZxingFromCanvas(reader, up, candidates);
        }
      }

      if (pickBestCode(candidates)) break;
    }

    return pickBestCode(candidates);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Decode a printed 1D ticket barcode from a screenshot/photo.
 * QR formats are excluded so Cabex Telegram/site QRs cannot win.
 */
export async function decode1dBarcodeFromImage(file: Blob): Promise<string> {
  const best = await withTimeout(decodeWithFormats(file, ONE_D_FORMATS), 15_000, "decode_timeout");
  if (!best) throw new Error("barcode_not_found");
  return best;
}

/** MAX WebView capture: QR + 1D from a photo (getUserMedia is often blocked in MAX). */
export async function decodeScanCodeFromImage(
  file: Blob,
): Promise<{ text: string; codeType: "qr" | "barcode" }> {
  // Prefer 1D so Cabex badge digits win over decorative page QRs on the same photo.
  const run = async () => {
    const oneD = await decodeWithFormats(file, ONE_D_FORMATS);
    if (oneD) return { text: oneD, codeType: "barcode" as const };
    const any = await decodeWithFormats(file, ANY_SCAN_FORMATS);
    if (!any) throw new Error("code_not_found");
    return { text: any, codeType: codeTypeFromText(any) };
  };
  return withTimeout(run(), 15_000, "decode_timeout");
}
